import { isEmpty } from 'lodash';
import { useCallback, useMemo } from 'react';

import { useOnWorkflowTrackerComplete } from '@/modules/workflowTrackers/hooks/useOnWorkflowTrackerComplete';
import { WorkflowTracker, WorkflowTrackerKind } from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';

import { useGetLatestPromptTestRunLazyQuery } from '../graphql/AdminPromptIterationPage.generated';

type Options = {
  promptId: string;
  enabled?: boolean;
  selectedDocumentId: string;
  onPreviewUpdated: (markdown: string | null) => void;
  clearLoading: () => void;
};

export function useOnPromptTestRunComplete({
  promptId,
  enabled = true,
  selectedDocumentId,
  onPreviewUpdated,
  clearLoading,
}: Options) {
  const [getLatestPromptTestRun] = useGetLatestPromptTestRunLazyQuery({
    fetchPolicy: 'network-only',
  });

  const handleCompleted = useCallback(
    async (completedTrackerIDs: string[]) => {
      try {
        diagnostics.debug(
          '[useOnPromptTestRunComplete] handleCompleted triggered',
          { completedTrackerIDs }
        );

        if (isEmpty(completedTrackerIDs)) {
          throw new Error('No completed tracker IDs in handleCompleted');
        }

        const completedTrackerID = completedTrackerIDs[0];
        const result = await getLatestPromptTestRun({
          variables: {
            promptID: promptId,
            documentID: selectedDocumentId,
            workflowTrackerID: completedTrackerID!,
          },
        });

        const testRun =
          result.data?.node?.__typename === 'DocumentSummaryPrompt'
            ? result.data.node.testRuns?.edges?.[0]?.node
            : null;

        if (!testRun || !testRun.summaryResult) {
          diagnostics.debug('[useOnPromptTestRunComplete] No test run found');
          onPreviewUpdated('(No summary generated)');
          return;
        }

        diagnostics.debug('[useOnPromptTestRunComplete] Test run found:', {
          testRunId: testRun.id,
          createdAt: testRun.createdAt,
        });

        onPreviewUpdated(testRun.summaryResult);
      } finally {
        clearLoading();
      }
    },
    [
      getLatestPromptTestRun,
      promptId,
      selectedDocumentId,
      onPreviewUpdated,
      clearLoading,
    ]
  );

  // Memoize filter to maintain stable reference - useOnWorkflowTrackerComplete
  // re-baselines when filter reference changes, which would prevent completion detection
  const trackerFilter = useMemo(
    () => (tracker: WorkflowTracker) => {
      if (tracker.kind !== WorkflowTrackerKind.DocumentSummaryPromptTestRun) {
        return false;
      }

      const run = tracker.documentSummaryPromptTestRun;
      if (run?.documentSummaryPrompt?.id !== promptId) {
        diagnostics.debug(
          '[useOnPromptTestRunComplete] Filtered out: wrong prompt',
          {
            expectedPromptId: promptId,
            actualPromptId: run?.documentSummaryPrompt?.id,
          }
        );
        return false;
      }

      return true;
    },
    [promptId]
  );

  useOnWorkflowTrackerComplete({
    kind: WorkflowTrackerKind.DocumentSummaryPromptTestRun,
    onCompleted: handleCompleted,
    enabled: enabled && !!promptId,
    filter: trackerFilter,
  });
}
